import moment from "moment";

/**
 * Pure helpers for the Members page.
 *
 * Intentionally uses structural types (no @coeff/api imports) so this module can be
 * unit-tested without ts-jest encountering @coeff/api's compiled JS — same pattern as
 * enterpriseBlocker.utils.ts.
 */

export type PlanCard = {
  planType: string;
  seats: number;
  subscriptionId?: string;
  /** The generated API client serializes datetimes as strings; moment() accepts both. */
  since?: string | Date;
  label: string;
};

type IndexSubscription = {
  plan_type?: string | null;
  max_users?: number | null;
  subscription_id?: string;
  subscription_active_since_dt?: string | Date;
};

type SubscriptionsPayload = {
  user_subscription?: { user_subscription?: IndexSubscription | null } | null;
  other_subscriptions?: (IndexSubscription | null | undefined)[] | null;
};

/**
 * Title-case a plan_type for display ("pro" → "Pro", "custom_x" → "Custom X").
 * Mirrors planNameFromType for the paid tiers that appear as plan cards; free tiers never
 * do (cards are built from real subscriptions).
 */
const planLabel = (planType: string): string =>
  planType
    .split("_")
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

/**
 * Build the plan summary cards for a domain: one card per subscription, oldest purchase
 * first. Same-tier subscriptions get numbered labels (Pro 1, Pro 2, …) so multiple plans of
 * the same tier aren't ambiguous; a unique tier keeps its plain name. Falls back to the
 * loaded subscription's details when the subscriptions payload is empty.
 */
export const buildPlanCards = (
  subscriptions: SubscriptionsPayload | null | undefined,
  subscriptionDetails: IndexSubscription | null | undefined
): PlanCard[] => {
  const list: PlanCard[] = [];
  const us = subscriptions?.user_subscription?.user_subscription;
  if (us?.plan_type) {
    list.push({
      planType: us.plan_type,
      seats: us.max_users ?? 0,
      subscriptionId: us.subscription_id,
      since: us.subscription_active_since_dt,
      label: "",
    });
  }
  (subscriptions?.other_subscriptions ?? []).forEach(s => {
    // The API can repeat the caller's own subscription inside other_subscriptions —
    // dedupe by id or the plan renders twice.
    if (s?.plan_type && !list.some(c => c.subscriptionId === s.subscription_id)) {
      list.push({
        planType: s.plan_type,
        seats: s.max_users ?? 0,
        subscriptionId: s.subscription_id,
        since: s.subscription_active_since_dt,
        label: "",
      });
    }
  });
  if (list.length === 0 && subscriptionDetails?.plan_type) {
    list.push({
      planType: subscriptionDetails.plan_type,
      seats: subscriptionDetails.max_users ?? 0,
      subscriptionId: subscriptionDetails.subscription_id,
      since: subscriptionDetails.subscription_active_since_dt,
      label: "",
    });
  }
  list.sort((a, b) => moment(a.since ?? 0).valueOf() - moment(b.since ?? 0).valueOf());
  const tierTotals: Record<string, number> = {};
  list.forEach(p => {
    tierTotals[p.planType] = (tierTotals[p.planType] ?? 0) + 1;
  });
  const tierSeen: Record<string, number> = {};
  list.forEach(p => {
    tierSeen[p.planType] = (tierSeen[p.planType] ?? 0) + 1;
    p.label =
      tierTotals[p.planType] > 1
        ? `${planLabel(p.planType)} ${tierSeen[p.planType]}`
        : planLabel(p.planType);
  });
  return list;
};

/** Up-to-two-letter avatar monogram ("Ada Wong" → "AW", "ada@x.com" → "A"). */
export const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map(n => n[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

/**
 * Selection key for a pending invite. Invites have no user_id yet, so rows are keyed by
 * email under a prefix that cannot collide with real user ids.
 */
export const inviteKey = (email: string) => `invite:${email}`;

/** Seats assignable right now on a subscription. */
export const assignableSeats = (details: { max_users?: number | null }): number =>
  details.max_users ?? 0;

/**
 * `scheduled_change_type` value for a pending end-of-term license reduction. Mirrors
 * `SubscriptionScheduledChangeType.DowngradeNumLicenses` — this module stays free of
 * `@coeff/api` imports (see header).
 */
const DOWNGRADE_NUM_LICENSES = "downgrade_num_licenses";

type PendingDropDetails = {
  max_users?: number | null;
  has_scheduled_changes?: boolean | null;
  scheduled_change_type?: string | null;
  scheduled_changes_info?: { num_licenses?: number | null } | null;
};

export type PendingLicenseDrop = {
  /** A downgrade_num_licenses change is pending in Chargebee. */
  isPending: boolean;
  /**
   * Licenses the plan will hold at renewal. Seat removals take effect end-of-term, so a
   * further removal stacks onto this, not onto today's `max_users` — a max_users-based total
   * sent to the backend overwrites the pending change and hands licenses back (removing 1 from
   * a 4→2 plan would land on 3, not 1). Equals `max_users` when nothing is pending or the
   * payload is malformed, and is clamped to `max_users` so bad data can never inflate capacity.
   *
   * Never gated: the pending change is real in Chargebee whichever way the rollout flag falls,
   * so gating this would reintroduce the overwrite bug for un-flagged plans.
   */
  effectiveLicenses: number;
  /**
   * True only for a real, well-formed drop below today's count — gates copy that has to stay
   * truthful about the ungated seat math above (e.g. explaining why nothing is removable),
   * and excludes malformed payloads that would read "4 licenses now, dropping to 4".
   */
  dropsSeats: boolean;
  /**
   * Whether to surface the scheduled drop to the user (banners, hints, confirm-step detail).
   * `dropsSeats` plus the backend's `enable_scheduled_license_reduction_enforcement` decision,
   * so the disclosure UI — including the "Undo" action, which the API rejects
   * when the flag is off — rolls out with the enforcement rather than ahead of it.
   */
  showsDropNotice: boolean;
};

/**
 * The single source of pending-downgrade seat math for Billing surfaces. ManageTeam and
 * AddSeatsModal both size removals against `effectiveLicenses`; keeping the formula here keeps
 * their two flows from drifting.
 */
export const getPendingLicenseDrop = (
  details: PendingDropDetails | null | undefined
): PendingLicenseDrop => {
  const maxUsers = details?.max_users ?? 0;
  const isPending =
    Boolean(details?.has_scheduled_changes) &&
    details?.scheduled_change_type === DOWNGRADE_NUM_LICENSES;
  const scheduledLicenses = details?.scheduled_changes_info?.num_licenses;
  // scheduled_changes_info is untyped JSON over the wire; anything non-numeric counts as
  // malformed rather than propagating NaN into seat counts.
  const effectiveLicenses =
    isPending && typeof scheduledLicenses === "number" && Number.isFinite(scheduledLicenses)
      ? Math.min(scheduledLicenses, maxUsers)
      : maxUsers;
  const dropsSeats = isPending && effectiveLicenses < maxUsers;
  return {
    isPending,
    effectiveLicenses,
    dropsSeats,
    showsDropNotice: dropsSeats,
  };
};
